import { useEffect, useState } from "react";
import Lenis from "lenis";
import { AnimatePresence } from "framer-motion";
import { Header } from "@/components/magical/Header";
import { Hero } from "@/components/magical/Hero";
import { CharacterSection } from "@/components/magical/CharacterSection";
import { EpisodeTheatre } from "@/components/magical/EpisodeTheatre";
import { SongSection } from "@/components/magical/SongSection";
import { ShopSection } from "@/components/magical/ShopSection";
import { StoryTrust } from "@/components/magical/StoryTrust";
import { FanArtSection } from "@/components/magical/FanArtSection";
import { NewsletterFooter } from "@/components/magical/NewsletterFooter";
import { CartDrawer, ProductModal } from "@/components/magical/Overlays";
import { CheckoutModal } from "@/components/magical/CheckoutModal";
import { AuthModal } from "@/components/magical/AuthModal";
import { OfficialSongPlayer } from "@/components/magical/OfficialSongPlayer";
import { useAuth } from "@/context/AuthContext";
import { EPISODES } from "@/data/magicalContent";
import { scrollToId } from "@/utils/scroll";
import { trackEvent } from "@/utils/track";
import "./MagicalApp.css";

const SHOW_STORE = false;

export default function MagicalApp() {
  const { user, getAccountState, saveAccountState } = useAuth();
  const [selectedEpisode, setSelectedEpisode] = useState(15);
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [productOpen, setProductOpen] = useState(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [authReason, setAuthReason] = useState("");
  const [checkoutIntent, setCheckoutIntent] = useState(false);
  const [cart, setCart] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [accountLoaded, setAccountLoaded] = useState(false);

  useEffect(() => {
    document.title = "Niyati & Shivu · Magical World";
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__NIYATI_LENIS__ = lenis;
    let frame;
    const raf = (time) => { lenis.raf(time); frame = requestAnimationFrame(raf); };
    frame = requestAnimationFrame(raf);
    return () => { cancelAnimationFrame(frame); delete window.__NIYATI_LENIS__; lenis.destroy(); };
  }, []);

  useEffect(() => {
    if (!user) { setAccountLoaded(false); return; }
    let active = true;
    getAccountState().then((state) => {
      if (!active) return;
      setWishlist((current) => Array.from(new Set([...(state.wishlist || []), ...current])));
      setCart((current) => [...(state.cart || []), ...current]);
      setAccountLoaded(true);
    }).catch(() => setAccountLoaded(true));
    return () => { active = false; };
  }, [user, getAccountState]);

  useEffect(() => {
    if (user && accountLoaded) saveAccountState({ wishlist, cart }).catch(() => {});
  }, [wishlist, cart, user, accountLoaded, saveAccountState]);

  useEffect(() => {
    if (user && accountLoaded && checkoutIntent) {
      setAuthOpen(false);
      setCartOpen(true);
      setCheckoutIntent(false);
    }
  }, [user, accountLoaded, checkoutIntent]);

  const selectEpisode = (number) => {
    setSelectedEpisode(number);
    trackEpisodePlay(number);
    scrollToId("episodes");
  };
  const trackEpisodePlay = (number) => {
    const episode = EPISODES.find((item) => item.number === number);
    if (episode && !episode.comingSoon) trackEvent("play", { media: `episode-${number}` });
  };
  const addToCart = (product) => { setCart((items) => [...items, product]); setCartOpen(true); };
  const toggleWishlist = (id) => setWishlist((items) => items.includes(id) ? items.filter((item) => item !== id) : [...items, id]);
  const openCheckout = () => {
    if (!user) {
      setCartOpen(false);
      setCheckoutIntent(true);
      setAuthReason("Create an account or log in to continue checkout and receive magical updates.");
      setAuthOpen(true);
      return;
    }
    setCheckoutOpen(true);
  };

  return (
    <div className="ns-app" data-testid="magical-app">
      <Header user={user} cartCount={cart.length} wishlistCount={wishlist.length} showStore={SHOW_STORE} onCartOpen={() => setCartOpen(true)} onWishlistOpen={() => scrollToId("shop")} onAccountOpen={() => { setAuthReason("Create an account to hear about new episodes, songs, and magical updates."); setAuthOpen(true); }} />
      <main>
        <Hero onWatchFinale={() => selectEpisode(15)} />
        <CharacterSection />
        <EpisodeTheatre selectedNumber={selectedEpisode} onSelectEpisode={(number) => { setSelectedEpisode(number); trackEpisodePlay(number); }} />
        <SongSection />
        {SHOW_STORE && <ShopSection wishlist={wishlist} onToggleWishlist={toggleWishlist} onQuickView={setProductOpen} onAddToCart={addToCart} />}
        <StoryTrust />
        <FanArtSection />
        <NewsletterFooter />
      </main>
      <OfficialSongPlayer />
      <AnimatePresence>
        {SHOW_STORE && cartOpen && <CartDrawer cart={cart} onClose={() => setCartOpen(false)} onRemove={(index) => setCart(cart.filter((_, itemIndex) => itemIndex !== index))} onCheckout={openCheckout} />}
        {SHOW_STORE && productOpen && <ProductModal product={productOpen} onClose={() => setProductOpen(null)} onAddToCart={addToCart} />}
        {SHOW_STORE && checkoutOpen && <CheckoutModal cart={cart} onClose={() => setCheckoutOpen(false)} onOrderComplete={() => { setCart([]); setCartOpen(false); }} />}
        {authOpen && <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} reason={authReason} wishlistCount={wishlist.length} cartCount={cart.length} />}
      </AnimatePresence>
    </div>
  );
}
